import { audio } from './audio';
import {
  BALL_R,
  BLAST_COUNT,
  BRICK_FILL,
  BRICK_POINTS,
  DROP_RATE,
  FIELD,
  HUD_H,
  LASER_COOLDOWN,
  MAX_BALLS,
  PADDLE_H,
  PADDLE_SPEED,
  PADDLE_Y,
  POWER_LABEL,
  RALLY_CAP,
  RALLY_STEP,
  SHIELD_Y,
  START_LIVES,
  TIMED_IDS,
  WALL,
} from './constants';
import { bounceNormal, circleAabb, circleObb, nudgeUnstuck, paddleBounce } from './collision';
import {
  bricksInBlast,
  countDestructible,
  isDestructible,
  isLevelClear,
  neighborCells,
  parseLevel,
  updateMovers,
} from './bricks';
import { getLevel, LEVELS } from './levels';
import { burst } from './particles';
import { tickParticles } from './particles';
import { createBall } from './ball';
import { clampPaddleX } from './paddle';
import { cloneBall, integrateBall, setBallSpeed } from './physics';
import {
  ballSpeedMul,
  collectPower,
  extraBallsToSpawn,
  hasTimed,
  paddleWidthMul,
  rollPowerId,
  shouldDrop,
  tickPowers,
  emptyPowerState,
} from './powerups';
import { drawWorld } from './render';
import { brickScore, extraLivesEarned, levelClearBonus, loadHighScore, loadUnlocked, saveHighScore, unlockAfterClear, comboMultiplier, clampLevelId } from './scoring';
import type {
  Ball,
  Brick,
  HudSnapshot,
  LaserShot,
  LevelDef,
  Paddle,
  Particle,
  Phase,
  Pickup,
  PowerState,
  Rotator,
} from './types';

export interface EngineCallbacks {
  onHud: (hud: HudSnapshot) => void;
}

export class BreakerEngine {
  private ctx: CanvasRenderingContext2D;
  private cb: EngineCallbacks;
  private raf = 0;
  private running = false;
  private last = 0;
  private cssW = 960;
  private cssH = 640;
  private scale = 1;
  private ox = 0;
  private oy = 0;
  private dpr = 1;

  phase: Phase = 'menu';
  score = 0;
  highScore = 0;
  lives = START_LIVES;
  combo = 0;
  levelId = 1;
  muted = audio.enabled === false;
  private claimedLives = new Set<number>();
  private levelBonus = 0;
  private lifeBonus = 0;
  private isNewHigh = false;
  private reportedPlay = false;
  runStartedAt = 0;

  private bricks: Brick[] = [];
  private balls: Ball[] = [];
  private paddle: Paddle = { x: 0, y: PADDLE_Y, w: 180, h: PADDLE_H, baseW: 180 };
  private pickups: Pickup[] = [];
  private lasers: LaserShot[] = [];
  private rotators: Rotator[] = [];
  private particles: Particle[] = [];
  private powers = emptyPowerState();
  private keys = { left: false, right: false };
  private pointerX: number | null = null;
  private shake = 0;
  private time = 0;
  private rally = 0;
  private laserCd = 0;
  private startTimer = 0;
  private dropMul = 1;
  private baseSpeed = 4.2;
  private pendingExtra = 0;
  runStartLevel = 1;
  private unlocked = 0;
  private def: LevelDef = LEVELS[0];

  constructor(ctx: CanvasRenderingContext2D, cb: EngineCallbacks) {
    this.ctx = ctx;
    this.cb = cb;
    this.highScore = loadHighScore();
    this.unlocked = loadUnlocked();
    this.muted = !audio.enabled;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
    this.emit();
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.cssW = cssW;
    this.cssH = cssH;
    this.dpr = dpr;
    this.scale = Math.min(cssW / FIELD.w, cssH / FIELD.h);
    this.ox = (cssW - FIELD.w * this.scale) / 2;
    this.oy = (cssH - FIELD.h * this.scale) / 2;
  }

  screenToField(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - this.ox) / this.scale, y: (sy - this.oy) / this.scale };
  }

  setPointer(sx: number | null): void {
    this.pointerX = sx == null ? null : this.screenToField(sx, 0).x;
  }

  setKey(code: string, down: boolean): void {
    if (code === 'ArrowLeft' || code === 'KeyA') this.keys.left = down;
    if (code === 'ArrowRight' || code === 'KeyD') this.keys.right = down;
  }

  startGame(levelId = 1): void {
    const id = clampLevelId(levelId);
    this.score = 0;
    this.lives = START_LIVES;
    this.combo = 0;
    this.claimedLives = new Set();
    this.isNewHigh = false;
    this.reportedPlay = false;
    this.runStartedAt = performance.now();
    this.runStartLevel = id;
    this.loadLevel(id, true);
  }

  openLevelSelect(): void {
    this.phase = 'levelSelect';
    this.emit();
  }

  goMenu(): void {
    this.phase = 'menu';
    this.emit();
  }

  pause(): void {
    if (this.phase === 'playing') this.phase = 'paused';
    this.emit();
  }

  resume(): void {
    if (this.phase === 'paused') this.phase = 'playing';
    this.emit();
  }

  restartLevel(): void {
    this.loadLevel(this.levelId, false);
  }

  toggleMute(): void {
    this.muted = !audio.toggle();
    this.emit();
  }

  launchOrAction(): void {
    if (this.phase === 'menu') {
      this.openLevelSelect();
      return;
    }
    if (this.phase === 'levelSelect') return;
    if (this.phase === 'paused') {
      this.resume();
      return;
    }
    if (this.phase === 'levelComplete') {
      this.advance();
      return;
    }
    if (this.phase === 'lifeLost') {
      this.serve();
      this.phase = 'playing';
      this.emit();
      return;
    }
    if (this.phase === 'gameOver' || this.phase === 'victory') {
      this.startGame(this.runStartLevel);
      return;
    }
    if (this.phase !== 'playing') return;
    const attached = this.balls.find((b) => b.attached);
    if (attached) {
      attached.attached = false;
      attached.vx = 0;
      attached.vy = -this.currentSpeed();
      if (this.pendingExtra > 0) {
        const n = extraBallsToSpawn(this.balls.length, this.pendingExtra, MAX_BALLS);
        for (let i = 0; i < n; i++) {
          const a = -Math.PI / 2 + (i - n / 2) * 0.4;
          this.balls.push(cloneBall(attached, a, this.currentSpeed()));
        }
        this.pendingExtra = 0;
      }
      audio.play('activate');
      return;
    }
    if (hasTimed(this.powers, 'laser')) this.fireLasers();
  }

  hud(): HudSnapshot {
    return {
      phase: this.phase,
      level: this.levelId,
      levelName: this.def.name,
      subtitle: this.def.subtitle,
      score: this.score,
      highScore: this.highScore,
      lives: this.lives,
      bricksLeft: countDestructible(this.bricks),
      combo: this.combo,
      multiplier: comboMultiplier(Math.max(1, this.combo)),
      activePowers: [
        ...TIMED_IDS.filter((id) => hasTimed(this.powers, id)).map((id) => ({
          id,
          label: POWER_LABEL[id],
          remainingMs: this.powers.timed[id] ?? 0,
        })),
        ...(this.powers.shield ? [{ id: 'shield' as const, label: 'Shield', remainingMs: 0 }] : []),
      ],
      muted: this.muted,
      levelBonus: this.levelBonus,
      lifeBonus: this.lifeBonus,
      isNewHigh: this.isNewHigh,
      serving: this.balls.some((b) => b.attached),
      unlocked: this.unlocked,
    };
  }

  private emit(): void {
    this.cb.onHud(this.hud());
  }

  private tick = (now: number): void => {
    if (!this.running) return;
    const dt = Math.min(0.033, (now - this.last) / 1000);
    this.last = now;
    if (this.phase === 'playing') this.update(dt);
    else if (this.phase === 'lifeLost' || this.phase === 'levelStart') this.holdServe(dt);
    if (this.phase === 'levelStart') {
      this.startTimer -= dt;
      if (this.startTimer <= 0) {
        this.phase = 'playing';
        this.emit();
      }
    }
    this.draw();
    this.raf = requestAnimationFrame(this.tick);
  };

  private holdServe(dt: number): void {
    this.paddle.w = this.paddle.baseW * paddleWidthMul(this.powers);
    this.movePaddle(dt);
    for (const ball of this.balls) {
      if (!ball.attached) continue;
      ball.x = this.paddle.x + this.paddle.w / 2;
      ball.y = this.paddle.y - ball.r - 2;
    }
  }

  private loadLevel(id: number, freshRun: boolean): void {
    this.def = getLevel(id);
    this.levelId = id;
    this.bricks = parseLevel(this.def);
    this.rotators = (this.def.rotators ?? []).map((r) => ({
      x: r.x,
      y: r.y,
      length: r.length,
      thickness: r.thickness,
      angle: r.phase ?? 0,
      speed: r.speed,
    }));
    this.pickups = [];
    this.lasers = [];
    this.particles = [];
    this.powers = emptyPowerState();
    this.rally = 0;
    this.combo = 0;
    this.dropMul = this.def.dropRateMul ?? 1;
    this.baseSpeed = this.def.ballSpeed;
    this.paddle.baseW = this.def.paddleWidth;
    this.paddle.w = this.def.paddleWidth;
    this.paddle.x = (FIELD.w - this.paddle.w) / 2;
    this.paddle.y = PADDLE_Y;
    this.balls = [];
    this.spawnBall(true);
    this.pendingExtra = this.def.startExtraBalls ?? 0;
    this.phase = 'levelStart';
    this.startTimer = 0.85;
    if (freshRun) this.reportedPlay = false;
    this.emit();
  }

  private spawnBall(attached: boolean, vx = 0, vy = 0): Ball {
    const ball = createBall(this.paddle.x + this.paddle.w / 2, this.paddle.y - BALL_R - 2, attached);
    ball.vx = vx;
    ball.vy = vy;
    ball.fire = hasTimed(this.powers, 'fireBall');
    ball.piercing = hasTimed(this.powers, 'piercing');
    this.balls.push(ball);
    return ball;
  }

  private currentSpeed(): number {
    return this.baseSpeed * 60 * (1 + this.rally) * ballSpeedMul(this.powers);
  }

  private serve(): void {
    this.balls = [];
    this.spawnBall(true);
  }

  private advance(): void {
    if (this.levelId >= LEVELS.length) {
      this.victory();
      return;
    }
    this.loadLevel(this.levelId + 1, false);
  }

  private update(dt: number): void {
    this.time += dt;
    this.laserCd = Math.max(0, this.laserCd - dt * 1000);
    this.shake = Math.max(0, this.shake - dt * 28);
    this.powers = tickPowers(this.powers, dt * 1000);
    this.paddle.w = this.paddle.baseW * paddleWidthMul(this.powers);
    this.movePaddle(dt);
    updateMovers(this.bricks, this.time);
    for (const rot of this.rotators) rot.angle += rot.speed * dt;
    this.particles = tickParticles(this.particles, dt);

    for (const ball of this.balls) {
      ball.fire = hasTimed(this.powers, 'fireBall');
      ball.piercing = hasTimed(this.powers, 'piercing') && !ball.fire;
      if (ball.attached) {
        ball.x = this.paddle.x + this.paddle.w / 2;
        ball.y = this.paddle.y - ball.r - 2;
        ball.vx = 0;
        ball.vy = 0;
        continue;
      }
      setBallSpeed(ball, this.currentSpeed());
      integrateBall(ball, dt);
      this.collideWalls(ball);
      this.collidePaddle(ball);
      this.collideBricks(ball);
      this.collideRotators(ball);
      this.collideShield(ball);
      ball.trail.push({ x: ball.x, y: ball.y });
      if (ball.trail.length > 10) ball.trail.shift();
    }

    this.balls = this.balls.filter((b) => b.y < FIELD.h + 40);
    if (this.phase === 'playing' && this.balls.length === 0) this.loseLife();

    this.updatePickups(dt);
    this.updateLasers(dt);

    if (this.phase === 'playing' && isLevelClear(this.bricks)) this.completeLevel();
    this.highScore = Math.max(this.highScore, this.score);
  }

  private movePaddle(dt: number): void {
    let x = this.paddle.x;
    if (this.pointerX != null) x = this.pointerX - this.paddle.w / 2;
    if (this.keys.left) x -= PADDLE_SPEED * dt;
    if (this.keys.right) x += PADDLE_SPEED * dt;
    this.paddle.x = clampPaddleX(x, this.paddle.w);
  }

  private collideWalls(ball: Ball): void {
    if (ball.x - ball.r < WALL) {
      ball.x = WALL + ball.r;
      ball.vx = Math.abs(ball.vx);
      const n = nudgeUnstuck(ball.vx, ball.vy);
      ball.vx = n.vx;
      ball.vy = n.vy;
    }
    if (ball.x + ball.r > FIELD.w - WALL) {
      ball.x = FIELD.w - WALL - ball.r;
      ball.vx = -Math.abs(ball.vx);
      const n = nudgeUnstuck(ball.vx, ball.vy);
      ball.vx = n.vx;
      ball.vy = n.vy;
    }
    if (ball.y - ball.r < HUD_H + 4) {
      ball.y = HUD_H + 4 + ball.r;
      ball.vy = Math.abs(ball.vy);
    }
  }

  private collidePaddle(ball: Ball): void {
    const hit = circleAabb(ball.x, ball.y, ball.r, this.paddle);
    if (!hit.hit || ball.vy < 0) return;
    ball.y = this.paddle.y - ball.r - 0.5;
    if (hasTimed(this.powers, 'magnet')) {
      ball.attached = true;
      ball.vx = 0;
      ball.vy = 0;
      audio.play('paddle');
      return;
    }
    const bounced = paddleBounce(ball, this.paddle);
    const nudged = nudgeUnstuck(bounced.vx, bounced.vy);
    ball.vx = nudged.vx;
    ball.vy = -Math.abs(nudged.vy);
    this.combo = 0;
    this.rally = Math.min(RALLY_CAP, this.rally + RALLY_STEP);
    audio.play('paddle');
    this.emit();
  }

  private collideBricks(ball: Ball): void {
    for (const b of this.bricks) {
      if (!b.alive) continue;
      const hit = circleAabb(ball.x, ball.y, ball.r, b);
      if (!hit.hit) continue;
      if (b.type === 'i' || !ball.piercing) {
        if (!ball.fire || b.type === 'i') {
          const bounced = bounceNormal(ball.vx, ball.vy, hit.nx, hit.ny);
          const n = nudgeUnstuck(bounced.vx, bounced.vy);
          ball.vx = n.vx;
          ball.vy = n.vy;
          ball.x += hit.nx * (hit.depth + 0.5);
          ball.y += hit.ny * (hit.depth + 0.5);
        }
      }
      if (b.type === 'i') {
        audio.play('brick');
        continue;
      }
      this.hitBrick(b);
      if (!ball.piercing && !ball.fire) break;
    }
  }

  private collideRotators(ball: Ball): void {
    for (const rot of this.rotators) {
      const hit = circleObb(ball.x, ball.y, ball.r, rot.x, rot.y, rot.length, rot.thickness, rot.angle);
      if (!hit.hit) continue;
      const bounced = bounceNormal(ball.vx, ball.vy, hit.nx, hit.ny);
      const n = nudgeUnstuck(bounced.vx, bounced.vy);
      ball.vx = n.vx;
      ball.vy = n.vy;
      ball.x += hit.nx * (hit.depth + 1);
      ball.y += hit.ny * (hit.depth + 1);
      audio.play('brick');
    }
  }

  private collideShield(ball: Ball): void {
    if (!this.powers.shield || ball.vy < 0) return;
    if (ball.y + ball.r >= SHIELD_Y) {
      ball.y = SHIELD_Y - ball.r - 1;
      ball.vy = -Math.abs(ball.vy);
      this.powers.shield = false;
      audio.play('activate');
      this.shake = 6;
      this.emit();
    }
  }

  private hitBrick(b: Brick): void {
    b.hp -= 1;
    if (b.hp > 0) {
      audio.play('damage');
      this.particles.push(...burst(b.x + b.w / 2, b.y + b.h / 2, BRICK_FILL[b.type], 6));
      return;
    }
    this.destroyBrick(b, true);
  }

  private destroyBrick(b: Brick, canDrop: boolean): void {
    if (!b.alive) return;
    b.alive = false;
    b.hp = 0;
    this.combo += 1;
    const base = BRICK_POINTS[b.type === 'i' ? 'n' : b.type];
    this.addScore(brickScore(base, this.combo));
    this.particles.push(...burst(b.x + b.w / 2, b.y + b.h / 2, BRICK_FILL[b.type], 16));
    audio.play(b.type === 'e' ? 'explosion' : 'destroy');
    if (b.type === 'e') {
      this.shake = 10;
      for (const [r, c] of neighborCells(b.row, b.col)) {
        const n = this.bricks.find((x) => x.alive && x.row === r && x.col === c && x.type !== 'i');
        if (n) this.destroyBrick(n, false);
      }
    }
    if (canDrop && b.type !== 'i' && shouldDrop(b.type === 'b', this.pickups.length, this.dropMul)) {
      this.pickups.push({
        id: rollPowerId(),
        x: b.x + b.w / 2 - 14,
        y: b.y,
        w: 28,
        h: 28,
        vy: 140,
      });
    }
    this.emit();
  }

  private addScore(n: number): void {
    const prev = this.score;
    this.score += n;
    for (const _ of extraLivesEarned(prev, this.score, this.claimedLives)) {
      this.claimedLives.add(_);
      this.lives += 1;
      audio.play('life');
    }
    const saved = saveHighScore(this.score);
    if (saved > this.highScore) {
      this.highScore = saved;
      this.isNewHigh = true;
    }
  }

  private updatePickups(dt: number): void {
    const next: Pickup[] = [];
    for (const p of this.pickups) {
      p.y += p.vy * dt;
      if (p.y > FIELD.h) continue;
      const hit =
        p.x < this.paddle.x + this.paddle.w &&
        p.x + p.w > this.paddle.x &&
        p.y < this.paddle.y + this.paddle.h &&
        p.y + p.h > this.paddle.y;
      if (hit) {
        this.applyPickup(p.id);
        continue;
      }
      next.push(p);
    }
    this.pickups = next.slice(0, DROP_RATE.maxFalling);
  }

  private applyPickup(id: ReturnType<typeof rollPowerId>): void {
    audio.play('pickup');
    const result = collectPower(this.powers, id);
    this.powers = result.state;
    if (result.kind === 'score') this.addScore(result.score);
    if (result.kind === 'extraLife') {
      this.lives += 1;
      audio.play('life');
    }
    if (result.kind === 'shield') audio.play('activate');
    if (result.kind === 'timed') audio.play('activate');
    if (result.kind === 'multiBall') {
      const free = this.balls.filter((b) => !b.attached)[0] ?? this.balls[0];
      const n = extraBallsToSpawn(this.balls.length, 2 + Math.floor(Math.random() * 2), MAX_BALLS);
      for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (i - n / 2) * 0.35;
        this.balls.push(cloneBall(free, a, this.currentSpeed()));
      }
    }
    if (result.kind === 'brickBlast') {
      for (const b of bricksInBlast(this.bricks, BLAST_COUNT)) this.destroyBrick(b, false);
      this.shake = 8;
      audio.play('explosion');
    }
    this.emit();
  }

  private fireLasers(): void {
    if (this.laserCd > 0) return;
    this.laserCd = LASER_COOLDOWN;
    const y = this.paddle.y - 12;
    this.lasers.push(
      { x: this.paddle.x + 8, y, w: 4, h: 14, vy: -720 },
      { x: this.paddle.x + this.paddle.w - 12, y, w: 4, h: 14, vy: -720 },
    );
    audio.play('activate');
  }

  private updateLasers(dt: number): void {
    const next: LaserShot[] = [];
    for (const shot of this.lasers) {
      shot.y += shot.vy * dt;
      if (shot.y + shot.h < 0) continue;
      let consumed = false;
      for (const b of this.bricks) {
        if (!b.alive) continue;
        if (shot.x < b.x + b.w && shot.x + shot.w > b.x && shot.y < b.y + b.h && shot.y + shot.h > b.y) {
          if (b.type === 'i') {
            consumed = true;
            break;
          }
          this.hitBrick(b);
          consumed = true;
          break;
        }
      }
      if (!consumed) next.push(shot);
    }
    this.lasers = next;
  }

  private loseLife(): void {
    this.combo = 0;
    this.rally = 0;
    this.lives -= 1;
    this.shake = 12;
    audio.play('gameover');
    if (this.lives <= 0) {
      this.phase = 'gameOver';
      saveHighScore(this.score);
    } else {
      this.phase = 'lifeLost';
      this.serve();
    }
    this.emit();
  }

  private completeLevel(): void {
    const bonus = levelClearBonus(this.levelId, this.lives);
    this.levelBonus = bonus.levelBonus;
    this.lifeBonus = bonus.lifeBonus;
    this.addScore(bonus.total);
    this.unlocked = unlockAfterClear(this.levelId);
    audio.play('complete');
    this.shake = 8;
    if (this.levelId >= LEVELS.length) this.victory();
    else this.phase = 'levelComplete';
    this.emit();
  }

  private victory(): void {
    this.phase = 'victory';
    saveHighScore(this.score);
    audio.play('victory');
    this.shake = 14;
    this.emit();
  }

  private draw(): void {
    const { ctx } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.cssW, this.cssH);
    ctx.fillStyle = '#05070f';
    ctx.fillRect(0, 0, this.cssW, this.cssH);
    ctx.setTransform(this.dpr * this.scale, 0, 0, this.dpr * this.scale, this.dpr * this.ox, this.dpr * this.oy);
    drawWorld(ctx, {
      bricks: this.bricks,
      balls: this.balls,
      paddle: this.paddle,
      pickups: this.pickups,
      lasers: this.lasers,
      rotators: this.rotators,
      particles: this.particles,
      shield: this.powers.shield,
      shake: this.shake,
      hud: this.hud(),
    }, this.time);
  }
}

export { LEVELS };
